<template>
  <div class="dk-colayout">
    <main class="dk-colayout__main">
      <slot />
    </main>

    <footer class="dk-colayout__foot">
      <div class="dk-colayout__bar dk-colayout__bar--foot">
        <nav v-if="policies.length" class="dk-colayout__links">
          <a v-for="p in policies" :key="p.key" :href="`/policies/${p.key}`" @click="openPolicy($event, p)">{{
            p.title
          }}</a>
        </nav>
        <span class="dk-colayout__copy">© {{ year }} {{ storeName }}</span>
      </div>
    </footer>

    <CheckoutPolicyModal
      v-if="active"
      :title="active.title"
      :body="active.body"
      @close="active = null"
    />
  </div>
</template>

<script setup lang="ts">
// Checkout-funnel layout — the storefront chrome (announcement bar, nav header,
// full footer, cart drawer, promo popup) is deliberately absent here, the way
// Shopify's checkout drops the online-store theme: once a buyer is paying, every
// link back into browsing is a chance to lose the order.
//
// There is deliberately NO header bar either. A full-width branded bar with its
// own border reads as a second header stacked on whatever the page already opens
// with (the urgency bar on /checkout, the upsell heading on /upsell, the
// confirmation badge on /order) — extra chrome Shopify's checkout does not have.
// The store name still identifies the page in the footer, and the way back to the
// cart lives next to the order summary, where the thing it edits actually is.
//
// Used by /checkout, /upsell and /order/:token so the whole funnel keeps one
// frame — nothing flashes in and out between paying and the thank-you page.
import { computed, defineAsyncComponent, ref } from "vue"
import { plainClick, policyList, type Policy } from "../lib/policypopup"
import { useBootstrapStore } from "../stores/bootstrap"

const bootstrap = useBootstrapStore()

const storeName = computed(() => bootstrap.storeName || "Secure checkout")
const year = new Date().getFullYear()

// LAZY, and it has to be: this layout is a STATIC import of App.vue, so anything it
// pulls in lands in the entry chunk and its styles in the render-blocking sheet —
// both of which sit within a few hundred bytes of their CI budgets. Most buyers
// never open a policy, so the modal's markup and CSS ride their own chunk, fetched
// on the click. Same reasoning as PromoPopup.
const CheckoutPolicyModal = defineAsyncComponent(() => import("./CheckoutPolicyModal.vue"))

const active = ref<Policy | null>(null)

// This footer is HIDDEN on /checkout — that page renders the same links at the foot
// of its form column (see checkout.css) — so what this copy serves is /upsell and
// /order/:token, which have no footer of their own.
const policies = computed(() => policyList(bootstrap.storeSettings))

function openPolicy(e: MouseEvent, p: Policy) {
  if (!plainClick(e)) return
  e.preventDefault()
  active.value = p
}
</script>

<style scoped>
.dk-colayout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--dk-color-bg, #fff);
}
.dk-colayout__main {
  flex: 1;
}
.dk-colayout__bar {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}
.dk-colayout__foot {
  border-top: 1px solid color-mix(in srgb, currentColor 12%, transparent);
  margin-top: 3rem;
}
.dk-colayout__foot .dk-colayout__bar {
  padding-block: 1.25rem;
  flex-wrap: wrap;
  justify-content: space-between;
  font-size: 0.85rem;
}
.dk-colayout__links {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.dk-colayout__links a {
  color: inherit;
  opacity: 0.7;
  text-decoration: none;
}
.dk-colayout__links a:hover {
  opacity: 1;
  text-decoration: underline;
}
.dk-colayout__copy {
  opacity: 0.6;
}
</style>
